/**
 * Сервис для генерации QR-кодов
 */
import { CryptoService } from './cryptoService'
import type { QRTokenPayload } from './cryptoService'
import { useWorkerStore } from '../store/workerStore'

export interface QRGenerationResult {
  qrToken: string
  payload: QRTokenPayload
  timeUntilRefresh: number // секунд до обновления
}

export class QRGenerator {
  private static currentQR: QRGenerationResult | null = null
  
  /**
   * Генерирует QR токен для текущего сотрудника
   * Поддерживает два формата: JWT (legacy) и Binary (новый, 75 байт)
   */
  static async generateQR(): Promise<QRGenerationResult> {
    const state = useWorkerStore.getState()
    
    if (!state.employeeId || !state.role || !state.encryptedPrivateKey) {
      throw new Error('Сотрудник не авторизован')
    }
    
    // Валидация role
    if (state.role !== 'worker' && state.role !== 'admin') {
      throw new Error('Невалидная роль сотрудника')
    }
    
    // Валидация employeeId (должен быть UUID)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(state.employeeId)) {
      throw new Error('Невалидный формат ID сотрудника')
    }
    
    // Расшифровываем приватный ключ
    // В MVP используем простой пароль (employee_id)
    // В продакшне использовать более безопасный метод
    let privateKeyHex: string
    try {
      privateKeyHex = CryptoService.decryptPrivateKey(
        state.encryptedPrivateKey,
        state.employeeId
      )
    } catch (error) {
      throw new Error('Не удалось расшифровать приватный ключ. Возможно, данные повреждены. Попробуйте пройти онбординг заново.')
    }
    
    // Определяем формат токена
    const tokenFormat = CryptoService.getTokenFormat()
    
    console.log('[QRGenerator] Generating QR with format:', tokenFormat)
    
    let qrToken: string
    let payload: QRTokenPayload
    
    if (tokenFormat === 'totp') {
      // НОВЕЙШИЙ ФОРМАТ: TOTP+HMAC токен (16 байт = 32 hex)
      try {
        // Генерируем TOTP токен (автоматически получит shortId и secret)
        qrToken = await CryptoService.generateTOTPToken(
          state.employeeId,
          state.role,
          privateKeyHex,
          state.shortId || undefined
        )
        
        // Создаём payload совместимый с QRGenerationResult
        // QR код живёт 5 секунд (не 15!)
        const nowSec = Math.floor(Date.now() / 1000)
        const timestamp = Math.floor(nowSec / 5) * 5
        
        payload = {
          employee_id: state.employeeId,
          role: state.role,
          ts: timestamp,
          nonce: '', // Не используется в TOTP формате
          iat: timestamp
        }
        
        console.log('[QRGenerator] TOTP token generated:', {
          tokenLength: qrToken.length,
          expected: 32,
          format: 'TOTP+HMAC (16 bytes)'
        })
      } catch (error) {
        console.error('[QRGenerator] TOTP generation failed:', error)
        throw new Error(`Не удалось сгенерировать TOTP токен: ${error}`)
      }
    } else if (tokenFormat === 'binary') {
      // LEGACY: Бинарный токен (73 байт)
      try {
        // Получаем shortId из store (сохранен при онбординге или через TOTP service)
        let shortId = state.shortId
        
        if (!shortId) {
          console.log('[QRGenerator] ⚠️ shortId not in store, using temporary ID from UUID')
          // Используем временный shortId из UUID как fallback
          const uuidPrefix = state.employeeId.replace(/-/g, '').substring(0, 8)
          shortId = parseInt(uuidPrefix, 16) & 0xFFFFFFFF
          console.log('[QRGenerator] Generated temporary shortId:', shortId)
        }
        
        // Генерируем binary token с shortId (без network запроса!)
        qrToken = await CryptoService.generateBinaryJWT(
          state.employeeId,
          state.role,
          privateKeyHex,
          shortId
        )
        
        // Создаём payload совместимый с QRGenerationResult
        // QR код живёт 5 секунд (не 15!)
        const nowSec = Math.floor(Date.now() / 1000)
        const timestamp = Math.floor(nowSec / 5) * 5
        
        payload = {
          employee_id: state.employeeId,
          role: state.role,
          ts: timestamp,
          nonce: '', // Не используется в бинарном формате
          iat: timestamp
        }
        
        console.log('[QRGenerator] Binary token generated:', {
          tokenLength: qrToken.length,
          expectedLength: 150, // 75 bytes * 2 hex chars
          timestamp,
          shortId,
          employeeId: state.employeeId
        })
      } catch (error) {
        console.error('[QRGenerator] Failed to generate binary token, falling back to JWT:', error)
        // Fallback к JWT при ошибке
        payload = CryptoService.generateQRPayload(state.employeeId, state.role)
        qrToken = await CryptoService.generateJWT(payload, privateKeyHex)
      }
    } else {
      // LEGACY ФОРМАТ: JWT (320 байт)
      payload = CryptoService.generateQRPayload(state.employeeId, state.role)
      qrToken = await CryptoService.generateJWT(payload, privateKeyHex)
      
      console.log('[QRGenerator] JWT token generated:', {
        tokenLength: qrToken.length
      })
    }
    
    // Вычисляем время до следующего обновления (конец текущего слота)
    // QR код живёт 5 секунд!
    const nowSec = Math.floor(Date.now() / 1000)
    const slotEnd = payload.ts + 5
    const timeUntilRefresh = Math.max(0, slotEnd - nowSec)
    
    const result: QRGenerationResult = {
      qrToken,
      payload,
      timeUntilRefresh
    }
    
    QRGenerator.currentQR = result
    
    return result
  }
  
  /**
   * Получает текущий QR или генерирует новый если нужно
   */
  static async getCurrentQR(): Promise<QRGenerationResult> {
    const nowMs = Date.now()
    const nowSec = Math.floor(nowMs / 1000)
    
    // Если QR ещё не генерировался, генерируем новый
    if (!QRGenerator.currentQR) {
      return await QRGenerator.generateQR()
    }
    
    // Проверяем, истёк ли time slot текущего QR
    // QR валиден с payload.ts до payload.ts + 5 секунд
    const slotEnd = QRGenerator.currentQR.payload.ts + 5
    const timeUntilRefresh = Math.max(0, slotEnd - nowSec)
    
    // Если время истекло или осталось меньше 1 секунды, генерируем новый QR
    if (timeUntilRefresh <= 0) {
      console.log('[QRGenerator] Time slot expired, generating new QR', {
        currentTs: QRGenerator.currentQR.payload.ts,
        slotEnd,
        nowSec,
        timeUntilRefresh
      })
      return await QRGenerator.generateQR()
    }
    
    return {
      ...QRGenerator.currentQR,
      timeUntilRefresh
    }
  }
  
  /**
   * Очищает кеш QR
   */
  static clearCache(): void {
    QRGenerator.currentQR = null
  }
}

